import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import * as XLSX from "xlsx";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { useAuth } from "@/context/AuthContext";
import { NUM, fmtDate, sameOrNew } from "@/lib/format";
import { PageHeader, Empty, StatCard, Segmented } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calculator as CalcComponent } from "@/components/Calculator";
import { ClipboardCheck, Truck, Trash2, CheckCircle2, Ruler, Calculator, ListChecks, RefreshCw, Search, Package, Percent, Plus, UserRound, X, PackagePlus, FileSpreadsheet, Upload, Pencil, PackageX, ScanLine, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";
import { LiveCartBanner, ChangesAlert, ItemChangeBadge, ChangeHistory } from "@/components/prep/LivePrep";
import { CartChatSheet } from "@/components/live/CartChatSheet";
import { useCartLive, useTitleFlash, silenceBell } from "@/hooks/useCartLive";
import { playNotifySound } from "@/lib/sound";
import { LiveChangeAlert } from "@/components/prep/LiveChangeAlert";
import BarcodeScanner from "@/components/BarcodeScanner";
import BarcodeInput from "@/components/BarcodeInput";
import { StockSplit } from "@/components/StockSplit";

const PREP_UNITS = ["ADET", "METRE", "KİLO", "PAKET", "KUTU", "TOP", "RULO", "METRETÜL", "TAKIM", "ÇİFT"];

function PrepCard({ list, isAdmin, selected, onToggleSel, onToggleItem, onPatchItem, onPersist, onComplete, onDelete, onMarkAll, onEdit, onMarkOOS, onBulkOOS, onSetDelivered, onSetDeliveredFull, onChat, onAck }) {
  const [metrajOpen, setMetrajOpen] = useState(() => new Set());
  const toggleMetraj = (idx) =>
    setMetrajOpen((prev) => { const n = new Set(prev); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });
  const [noteOpen, setNoteOpen] = useState(() => new Set());
  const toggleNote = (idx) =>
    setNoteOpen((prev) => { const n = new Set(prev); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });

  const isLive = !!list.cart_id;
  const liveItems = list.items.filter((i) => !i.removed);
  const total = liveItems.length;
  const prepared = liveItems.filter((i) => i.prepared).length;
  const pendingChanges = list.items.filter((i) => i.change).length;
  const pct = total ? Math.round((prepared / total) * 100) : 0;
  const isDone = list.status === "tamamlandi";
  const oosCount = liveItems.filter((i) => i.out_of_stock).length;
  const delOf = (i) => (i.delivered != null ? Number(i.delivered) : (i.prepared ? (Number(i.quantity) || 0) : 0));
  const partialCount = liveItems.filter((i) => { const reqd = Number(i.quantity) || 0; const d = delOf(i); return d > 0 && d < reqd; }).length;
  // Eksik verilen: teslim adedi girilmiş / stokta yok / tamamlanmış listede istenenden az verilen kalemler
  const missingItems = liveItems.filter((i) => { const reqd = Number(i.quantity) || 0; return reqd > 0 && delOf(i) < reqd && (i.delivered != null || i.out_of_stock || isDone); });
  const missingQty = missingItems.reduce((s, i) => s + Math.max(0, (Number(i.quantity) || 0) - delOf(i)), 0);
  // Stok yetersiz: istenen adet, toplam stoktan (Depo + Mağaza) fazla olan stoklu kalemler
  const shortStock = liveItems.filter((i) => i.in_stock && !i.prepared && (Number(i.quantity) || 0) > (Number(i.stock_quantity) || 0)).length;
  const src = isLive ? { label: list.cart_status === "open" ? "Canlı Sepet" : "Sepet", cls: "border-red-300 bg-red-50 text-red-700" } : list.quick_sale_id ? { label: "Hızlı Satış", cls: "border-orange-300 bg-orange-50 text-orange-700" } : /^TKF/i.test(list.quote_no || "") ? { label: "Teklif", cls: "border-sky-300 bg-sky-50 text-sky-700" } : /CARİ|CARI/i.test(list.quote_no || "") ? { label: "Cari", cls: "border-violet-300 bg-violet-50 text-violet-700" } : { label: "Manuel", cls: "border-slate-300 bg-slate-50 text-slate-600" };
  const stripe = isDone ? "bg-[#16A34A]" : prepared > 0 ? "bg-sky-500" : "bg-amber-500";

  return (
    <div className={`relative bg-card border rounded-xl overflow-hidden transition-all hover:shadow-md ${selected ? "ring-2 ring-primary border-primary" : pendingChanges ? "ring-2 ring-orange-400 border-orange-400" : isLive && list.cart_status === "open" ? "border-red-300" : ""}`} data-testid={`prep-${list.id}`}>
      <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${stripe}`} />
      {isLive && <LiveCartBanner list={list} onChat={onChat} />}
      <div className="p-4 pl-5 border-b flex items-start gap-3">
        {isAdmin && <Checkbox checked={!!selected} onCheckedChange={() => onToggleSel(list.id)} className="mt-1.5" data-testid={`prep-sel-${list.id}`} />}
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center font-head font-black text-lg shrink-0 ${isDone ? "bg-[#16A34A]/15 text-[#16A34A]" : "bg-primary/10 text-primary"}`}>
          {list.customer_title?.[0]?.toLocaleUpperCase("tr") || "?"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-mono text-sm font-bold tabular-nums">{list.quote_no}</span>
            <Badge variant="outline" className={`text-[10px] py-0 ${src.cls}`}>{src.label}</Badge>
            {isDone ? <Badge className="bg-[#16A34A] hover:bg-[#16A34A] text-[10px]">Tamamlandı</Badge> : prepared > 0 ? <Badge className="bg-sky-500 hover:bg-sky-500 text-[10px]">Hazırlanıyor · %{pct}</Badge> : <Badge className="bg-amber-500 hover:bg-amber-500 text-[10px]">Bekliyor</Badge>}
            {oosCount > 0 && <Badge variant="outline" className="text-[10px] py-0 border-amber-500 text-amber-700 bg-amber-100/60"><PackageX className="h-3 w-3 mr-0.5" />{oosCount} stokta yok</Badge>}
            {partialCount > 0 && <Badge variant="outline" className="text-[10px] py-0 border-sky-400 text-sky-700 bg-sky-50" data-testid={`prep-partial-count-${list.id}`}>{partialCount} kısmi teslim</Badge>}
            {missingItems.length > 0 && <Badge className="bg-red-600 hover:bg-red-600 text-[10px]" data-testid={`prep-missing-count-${list.id}`}>{missingItems.length} ürün EKSİK VERİLDİ · {NUM(missingQty)} adet</Badge>}
            {!isDone && shortStock > 0 && <Badge variant="outline" className="text-[10px] py-0 border-red-300 text-red-700 bg-red-50" data-testid={`prep-short-stock-${list.id}`}>{shortStock} kalemde stok yetersiz</Badge>}
            {pendingChanges > 0 && <Badge className="bg-orange-600 hover:bg-orange-600 text-[10px]" data-testid={`prep-changed-badge-${list.id}`}>DEĞİŞİKLİK VAR · {pendingChanges}</Badge>}
          </div>
          <div className="font-head font-black text-[15px] mt-0.5 truncate" title={list.customer_title}>{list.customer_title}</div>
          <div className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="inline-flex items-center gap-1"><UserRound className="h-3 w-3" />{list.assigned_name}</span>
            <span>·</span><span>{fmtDate(list.created_at)}</span>
            <span>·</span><span className="tabular-nums">{total} kalem</span>
            {list.note && <><span>·</span><span className="truncate max-w-[220px]" title={list.note}>{list.note}</span></>}
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-0.5">
            {!isDone && !isLive && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(list)} title="Listeyi düzenle" data-testid={`prep-edit-${list.id}`}><Pencil className="h-4 w-4 text-muted-foreground" /></Button>}
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onDelete(list.id)} title="Sil" data-testid={`prep-del-${list.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
          </div>
        )}
      </div>

      <div className="p-4 pl-5">
        {(isLive || list.quick_sale_id) && list.note && (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2" data-testid={`prep-general-note-${list.id}`}>
            <StickyNote className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-[12.5px] font-semibold text-amber-900 whitespace-pre-wrap break-words"><span className="uppercase tracking-wider text-[10px] text-amber-600 font-black mr-1">Genel Not:</span>{list.note}</div>
          </div>
        )}
        <ChangesAlert list={list} count={pendingChanges} onAckAll={(l) => onAck(l, [])} />
        <div className="flex items-center gap-3 mb-3">
          <Progress value={pct} className={`h-2 ${isDone ? "[&>*]:bg-[#16A34A]" : prepared > 0 ? "[&>*]:bg-sky-500" : ""}`} />
          <span className="text-xs font-bold tabular-nums whitespace-nowrap">{prepared}/{total} <span className="text-muted-foreground font-normal">hazır</span></span>
        </div>

        {!isDone && (
          <div className="flex gap-2 mb-3 flex-wrap">
            <Button size="sm" variant="outline" className="flex-1 min-w-[180px]" onClick={() => onMarkAll(list, true)} data-testid={`prep-selectall-${list.id}`}>
              <ListChecks className="h-4 w-4 mr-1" />Tümünü Hazırlandı İşaretle
            </Button>
            {prepared > 0 && (
              <Button size="sm" variant="ghost" onClick={() => onMarkAll(list, false)} data-testid={`prep-clearall-${list.id}`}>Temizle</Button>
            )}
            {liveItems.some((i) => !i.out_of_stock) && (
              <Button size="sm" variant="outline" className="text-amber-600 border-amber-300 hover:bg-amber-50" onClick={() => onBulkOOS(list)} data-testid={`prep-bulk-oos-${list.id}`}>
                <PackageX className="h-4 w-4 mr-1" />Toplu Stokta Yok
              </Button>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          {list.items.map((it, idx) => {
            const showMetraj = metrajOpen.has(idx) || it.start_m != null || it.end_m != null;
            const used = (Number(it.end_m) || 0) - (Number(it.start_m) || 0);
            const reqd = Number(it.quantity) || 0;
            const delivered = it.delivered != null ? Number(it.delivered) : (it.prepared ? reqd : 0);
            const missing = Math.max(0, reqd - delivered);
            const partial = delivered > 0 && delivered < reqd;
            const stockQty = Number(it.stock_quantity) || 0;
            const critLvl = Number(it.critical_level) || 0;
            const stockCls = !it.in_stock ? "" : stockQty <= 0 ? "bg-red-50 text-red-700 border border-red-200" : (critLvl > 0 && stockQty <= critLvl) ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200";
            const hasMeta = it.code || it.brand || it.in_stock;
            const descDup = [it.brand, it.code].filter(Boolean).join(" · ") === (it.description || "");
            if (it.removed && !it.change) {
              return (
                <div key={idx} className="rounded-lg border border-dashed px-3 py-1.5 flex items-center gap-2 text-xs text-muted-foreground bg-muted/30" data-testid={`prep-item-removed-${list.id}-${idx}`}>
                  <X className="h-3.5 w-3.5" /><span className="line-through truncate flex-1">{it.name}</span><span className="font-semibold">Sepetten çıkarıldı</span>
                </div>
              );
            }
            const ch = it.change;
            return (
              <div key={idx} className={`rounded-lg border transition-colors ${ch ? (ch.kind === "removed" ? "bg-red-50/60 border-red-300" : "bg-orange-50/60 border-orange-300") : it.prepared ? "bg-[#16A34A]/5 border-[#16A34A]/30" : it.out_of_stock ? "bg-amber-50/50 border-amber-300" : ""}`} data-testid={`prep-item-${list.id}-${idx}`}>
                <div className="px-2.5 py-2 space-y-1.5">
                <div className="flex items-start gap-2.5">
                  <Checkbox className="mt-0.5" checked={!!it.prepared} onCheckedChange={() => !isDone && !it.removed && onToggleItem(list, idx)} disabled={isDone || it.removed} data-testid={`prep-check-${list.id}-${idx}`} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold leading-snug break-words ${it.prepared || it.removed ? "line-through text-muted-foreground" : ""}`}>{it.name}</div>
                    <ItemChangeBadge it={it} unit={it.unit} onAck={() => onAck(list, [it.key])} testid={`prep-change-${list.id}-${idx}`} />
                    {hasMeta && (
                      <div className="flex items-center gap-1 flex-wrap mt-1">
                        {it.code && <span className="text-[10.5px] leading-4 font-mono text-muted-foreground bg-muted px-1.5 rounded whitespace-nowrap" data-testid={`prep-item-code-${list.id}-${idx}`}>{it.code}</span>}
                        {it.brand && <span className="text-[10.5px] leading-4 font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 rounded whitespace-nowrap" data-testid={`prep-item-brand-${list.id}-${idx}`}>{it.brand}</span>}
                        {it.in_stock ? (
                          <>
                            <StockSplit depo={it.stock_depo ?? stockQty} magaza={it.stock_magaza || 0} unit={it.stock_unit} total testid={`prep-item-stock-${list.id}-${idx}`} />
                            {(stockQty <= 0 || (critLvl > 0 && stockQty <= critLvl) || reqd > stockQty) && (
                              <span className={`text-[10.5px] leading-4 font-semibold px-1.5 rounded whitespace-nowrap ${stockCls}`} data-testid={`prep-item-stockflag-${list.id}-${idx}`}>
                                {stockQty <= 0 ? "tükendi" : reqd > stockQty ? `stok yetersiz · ${NUM(Math.round((reqd - stockQty) * 100) / 100)} eksik` : "kritik"}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-[10.5px] leading-4 text-muted-foreground bg-muted px-1.5 rounded whitespace-nowrap" data-testid={`prep-item-nostock-${list.id}-${idx}`}>Stok kaydı yok</span>
                        )}
                      </div>
                    )}
                    {it.description && !descDup && !noteOpen.has(idx) && (
                      <div className="mt-1.5 flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-200 px-2 py-1.5" data-testid={`prep-item-desc-${list.id}-${idx}`}>
                        <StickyNote className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
                        <span className="text-xs font-semibold text-amber-800 whitespace-pre-wrap break-words leading-snug"><span className="uppercase tracking-wider text-[10px] text-amber-600 font-bold mr-1">Açıklama:</span>{" "}{it.description}</span>
                      </div>
                    )}
                    {!isDone && noteOpen.has(idx) && (
                      <div className="mt-1.5" data-testid={`prep-note-row-${list.id}-${idx}`}>
                        <textarea rows={2} value={it.description || ""} placeholder="Bu kalem için açıklama — ör. sarı-yeşil, 2. kat, acele"
                          onChange={(e) => onPatchItem(list, idx, { description: e.target.value })}
                          onBlur={() => onPersist(list)}
                          className="w-full rounded-md border border-amber-300 bg-amber-50/40 px-2 py-1.5 text-xs resize-y focus:outline-none focus:ring-2 focus:ring-amber-300"
                          data-testid={`prep-note-input-${list.id}-${idx}`} />
                      </div>
                    )}
                    {it.out_of_stock && (
                      <Badge variant="outline" className="mt-1 border-amber-500 text-amber-700 bg-amber-100/60 text-[10.5px] py-0" data-testid={`prep-oos-badge-${list.id}-${idx}`}>
                        <PackageX className="h-3 w-3 mr-1" />Stokta yok · sipariş talebi girildi
                      </Badge>
                    )}
                  </div>
                  <span className={`shrink-0 text-[13px] font-bold tabular-nums whitespace-nowrap px-2 py-0.5 rounded ${ch && ch.kind !== "removed" ? "bg-orange-600 text-white" : "bg-secondary/10 text-secondary"}`} data-testid={`prep-qty-${list.id}-${idx}`}>
                    {ch && ch.kind === "qty" && <s className="opacity-70 mr-1">{NUM(ch.from)}</s>}{it.removed ? <s>{NUM(ch?.from)}</s> : NUM(it.quantity)} {it.unit}
                  </span>
                </div>
                {!it.removed && <div className="flex items-center gap-1.5 flex-wrap">
                  {isDone ? (
                    <span className="text-xs text-muted-foreground">Verilen: <b className="text-sm text-foreground tabular-nums" data-testid={`prep-delivered-ro-${list.id}-${idx}`}>{NUM(delivered)} / {NUM(reqd)} {it.unit}</b></span>
                  ) : (
                    <>
                      <span className="hidden sm:inline text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">Verilen</span>
                      <Input type="number" min="0" inputMode="decimal" value={it.delivered ?? (it.prepared ? reqd : "")} placeholder="0" aria-label="Verilen miktar"
                        onChange={(e) => onSetDelivered(list, idx, e.target.value)}
                        onBlur={() => onPersist(list)}
                        className="h-8 w-16 px-2 tabular-nums" data-testid={`prep-delivered-${list.id}-${idx}`} />
                      <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">/ {NUM(reqd)} {it.unit}</span>
                      {delivered < reqd && (
                        <Button size="sm" variant="ghost" className="h-7 px-1.5 text-xs font-semibold text-[#16A34A] hover:text-[#15803d]" onClick={() => onSetDeliveredFull(list, idx)} data-testid={`prep-delivered-full-${list.id}-${idx}`}>Tümünü ver</Button>
                      )}
                    </>
                  )}
                  {partial && (
                    <Badge variant="outline" className="border-amber-500 text-amber-700 bg-amber-50 text-[10px] py-0 whitespace-nowrap" data-testid={`prep-missing-${list.id}-${idx}`}>Eksik: {NUM(missing)} {it.unit}</Badge>
                  )}
                  {reqd > 0 && delivered >= reqd && (
                    <Badge className="bg-[#16A34A] hover:bg-[#16A34A] text-[10px] py-0 whitespace-nowrap" data-testid={`prep-complete-badge-${list.id}-${idx}`}>Tam teslim</Badge>
                  )}
                  {!isDone && (
                    <div className="ml-auto flex items-center">
                      <Button size="icon" variant="ghost" className={`h-8 w-8 ${noteOpen.has(idx) || it.description ? "text-amber-600" : "text-muted-foreground"}`} onClick={() => toggleNote(idx)} title={it.description ? "Açıklamayı düzenle" : "Açıklama ekle"} data-testid={`prep-note-toggle-${list.id}-${idx}`}>
                        <StickyNote className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className={`h-8 w-8 ${it.out_of_stock ? "text-amber-600" : "text-muted-foreground"}`} onClick={() => onMarkOOS(list, idx, !it.out_of_stock)} title={it.out_of_stock ? "Stokta yok işaretini kaldır" : "Ürün stokta yok — sipariş talebi oluştur"} data-testid={`prep-oos-toggle-${list.id}-${idx}`}>
                        <PackageX className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className={`h-8 w-8 ${showMetraj ? "text-primary" : "text-muted-foreground"}`} onClick={() => toggleMetraj(idx)} title="Metraj gir" data-testid={`prep-metraj-toggle-${list.id}-${idx}`}>
                        <Ruler className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>}
                </div>
                {showMetraj && !it.removed && (
                  <div className="flex items-center gap-2 px-2.5 pb-2 flex-wrap">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Metraj</span>
                    <Input type="number" placeholder="Baş" value={it.start_m ?? ""} disabled={isDone}
                      onChange={(e) => onPatchItem(list, idx, { start_m: e.target.value === "" ? null : Number(e.target.value) })}
                      onBlur={() => onPersist(list)} className="h-8 w-24 tabular-nums" data-testid={`prep-start-${list.id}-${idx}`} />
                    <span className="text-muted-foreground">→</span>
                    <Input type="number" placeholder="Son" value={it.end_m ?? ""} disabled={isDone}
                      onChange={(e) => onPatchItem(list, idx, { end_m: e.target.value === "" ? null : Number(e.target.value) })}
                      onBlur={() => onPersist(list)} className="h-8 w-24 tabular-nums" data-testid={`prep-end-${list.id}-${idx}`} />
                    {it.start_m != null && it.end_m != null && used !== 0 && (
                      <Badge variant="outline" className="border-primary/40 text-primary" data-testid={`prep-used-${list.id}-${idx}`}>
                        Kullanılan: {NUM(Math.abs(used))} mt
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {isLive && <ChangeHistory list={list} />}
        {!isDone && (
          <Button className="w-full mt-4 bg-[#16A34A] hover:bg-[#15803d]" onClick={() => onComplete(list)} disabled={prepared < total || pendingChanges > 0} data-testid={`prep-complete-${list.id}`}>
            <CheckCircle2 className="h-4 w-4 mr-2" /> {pendingChanges > 0 ? `Önce ${pendingChanges} değişikliği onaylayın` : prepared < total ? `${total - prepared} kalem kaldı` : "Hazırlığı Tamamla"}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function PrepLists() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [lists, setLists] = useState([]);
  const [calcOpen, setCalcOpen] = useState(false);
  const [sel, setSel] = useState(new Set());
  const [search, setSearch] = useState("");
  const [view, setView] = useState("active");   // active | done | all
  const [chatList, setChatList] = useState(null);   // canlı sepet mesajlaşması açık liste
  const chatCl = useCartLive(chatList?.cart_id, { userId: user?.id });
  const prevSig = useRef(null);
  const [changeAlert, setChangeAlert] = useState(null);   // depo tableti alarmı: görülmemiş canlı sepet değişiklikleri

  const changeSigs = (rows) => {
    const m = new Map();
    rows.forEach((l) => (l.items || []).forEach((i) => {
      if (i.change) m.set(`${l.id}:${i.key}:${i.change.kind}:${i.change.to}`, { listId: l.id, customer: l.customer_title, owner: l.cart_owner_name, name: i.name, unit: i.unit, ...i.change });
    }));
    return m;
  };
  // Sepetten yeni mesaj → ses + bildirim; canlı sepet değişikliği → sessiz, küçük köşe bildirimi
  const notifyDiff = (rows) => {
    const unread = rows.reduce((a, l) => a + (l.msg_unread || 0), 0);
    const sigs = changeSigs(rows);
    const prev = prevSig.current;
    prevSig.current = { unread, sigs };
    setChangeAlert((a) => { if (!a) return a; const keep = a.filter((x) => sigs.has(x.sig)); return keep.length === a.length ? a : (keep.length ? keep : null); });
    if (!prev) return;
    if (unread > prev.unread) {
      const l = rows.find((x) => x.msg_unread > 0);
      playNotifySound(); silenceBell("pos_mesaj");
      toast.message("Sepetten yeni mesaj", { description: l ? `${l.cart_owner_name || ""} · ${l.customer_title}` : "", duration: 12000, action: l ? { label: "Aç", onClick: () => openChat(l) } : undefined });
    }
    const fresh = [...sigs.entries()].filter(([k]) => !prev.sigs.has(k)).map(([sig, v]) => ({ sig, ...v }));
    if (fresh.length) {
      silenceBell("hazirlik");
      setChangeAlert((a) => [...(a || []).filter((x) => sigs.has(x.sig)), ...fresh]);
    }
  };
  const showList = (id) => {
    setView("active"); setSearch("");
    setTimeout(() => document.querySelector(`[data-testid="prep-${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
  };
  const load = () => api.get("/prep-lists").then((r) => { notifyDiff(r.data || []); setLists(sameOrNew(r.data)); }).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line
  useAutoRefresh(load);
  useTitleFlash("prep-page", chatList ? 0 : lists.reduce((a, l) => a + (l.msg_unread || 0), 0));
  const openChat = (list) => {
    setLists((prev) => prev.map((l) => (l.id === list.id ? { ...l, msg_unread: 0 } : l)));
    if (prevSig.current) prevSig.current.unread = Math.max(0, prevSig.current.unread - (list.msg_unread || 0));
    setChatList(list);
  };
  const ack = async (list, keys) => {
    try { await api.post(`/prep-lists/${list.id}/ack`, { keys }); toast.success("Değişiklik onaylandı"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const [workers, setWorkers] = useState([]);
  const [newDlg, setNewDlg] = useState(false);
  const [nf, setNf] = useState({ title: "", assigned: "all", note: "", items: [] });
  const [stockQ, setStockQ] = useState("");
  const [stockRes, setStockRes] = useState([]);
  const [bcText, setBcText] = useState("");
  const [completeTarget, setCompleteTarget] = useState(null);
  const [oosDlg, setOosDlg] = useState(null);
  const [oosSel, setOosSel] = useState(new Set());
  const [editDlg, setEditDlg] = useState(false);
  const [ef, setEf] = useState({ id: null, title: "", assigned: "all", note: "", items: [] });
  const editFileRef = useRef(null);
  const fileRef = useRef(null);
  useEffect(() => { if (isAdmin) api.get("/users").then((r) => setWorkers(r.data.filter((u) => u.role === "worker"))).catch(() => {}); }, [isAdmin]);
  useEffect(() => {
    if (!newDlg) return undefined;
    const t = stockQ.trim();
    if (!t) { setStockRes([]); return undefined; }
    const id = setTimeout(() => {
      api.get(`/products/browse?q=${encodeURIComponent(t)}&page=1&page_size=8`).then((r) => setStockRes(r.data.items || [])).catch(() => {});
    }, 300);
    return () => clearTimeout(id);
  }, [stockQ, newDlg]);
  const openNew = () => { setNf({ title: "", assigned: "all", note: "", items: [] }); setStockQ(""); setStockRes([]); setBcText(""); setNewDlg(true); };
  const addStock = (p) => { setNf((f) => ({ ...f, items: [...f.items, { name: p.name, description: "", brand: p.brand || "", code: p.code || "", quantity: 1, unit: p.unit || "ADET", product_id: p.id }] })); setStockQ(""); setStockRes([]); };
  // Barkod(lar) → kalem: aynı ürün tekrar okutulursa adedi artar (yeni satır açılmaz)
  const addCodes = async (codes) => {
    if (!codes.length) return 0;
    try {
      const { data } = await api.post("/products/by-barcodes", { barcodes: codes });
      const matched = data.matched || [];
      const keys = (p) => [p.barcode, p.code, ...(p.barcodes || [])].map((k) => String(k || "").trim()).filter(Boolean);
      const counts = new Map();
      codes.forEach((c) => { const p = matched.find((m) => keys(m).includes(c)); if (p) counts.set(p.id, (counts.get(p.id) || 0) + 1); });
      setNf((f) => {
        const items = [...f.items];
        matched.forEach((p) => {
          const n = counts.get(p.id) || 1;
          const i = items.findIndex((it) => it.product_id === p.id);
          if (i >= 0) items[i] = { ...items[i], quantity: (Number(items[i].quantity) || 0) + n };
          else items.push({ name: p.name, description: "", brand: p.brand || "", code: p.code || "", quantity: n, unit: p.unit || "ADET", product_id: p.id });
        });
        return { ...f, items };
      });
      const un = data.unmatched || [];
      if (un.length) toast.warning(`${matched.length} ürün eklendi · sistemde olmayan barkod: ${un.slice(0, 5).join(", ")}${un.length > 5 ? "…" : ""}`);
      else toast.success(matched.length === 1 ? `Eklendi: ${matched[0].name}` : `${matched.length} ürün barkodla eklendi`);
      return matched.length;
    } catch (e) { toast.error(apiError(e)); return 0; }
  };
  const addByBarcodes = async () => {
    const codes = bcText.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
    if (!codes.length) return;
    await addCodes(codes);
    setBcText("");
  };
  const [bcCam, setBcCam] = useState(false);
  const addManual = () => setNf((f) => ({ ...f, items: [...f.items, { name: "", description: "", quantity: 1, unit: "ADET" }] }));
  const setNfItem = (i, k, v) => setNf((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));
  const rmNfItem = (i) => setNf((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const createList = async () => {
    const items = nf.items.filter((it) => (it.name || "").trim()).map((it) => ({ ...it, quantity: Number(it.quantity) || 0 }));
    if (!items.length) return toast.error("En az bir kalem ekleyin");
    try {
      await api.post("/prep-lists", { title: nf.title.trim() || "Hazırlık Listesi", label: "MANUEL", assigned_to: nf.assigned === "all" ? null : nf.assigned, note: nf.note || "", items });
      toast.success("Hazırlanacak liste depoya gönderildi");
      setNewDlg(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const unitOpts = (u) => (u && !PREP_UNITS.includes(u) ? [u, ...PREP_UNITS] : PREP_UNITS);
  const add5Manual = () => setNf((f) => ({ ...f, items: [...f.items, ...Array.from({ length: 5 }, () => ({ name: "", description: "", quantity: 1, unit: "ADET" }))] }));
  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([["Ürün Adı", "Miktar", "Birim", "Açıklama"], ["Örnek: 6mm2 NYA Kablo", 50, "METRE", "Sarı-yeşil"]]);
    ws["!cols"] = [{ wch: 36 }, { wch: 10 }, { wch: 12 }, { wch: 30 }];
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Hazırlık");
    XLSX.writeFile(wb, "hazirlik-listesi-sablonu.xlsx");
    toast.success("Şablon indirildi — doldurup 'Excel Yükle' ile ekleyin");
  };
  const onExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
      const items = rows.map((r) => {
        const name = String(r["Ürün Adı"] ?? r["Urun Adi"] ?? r["Ad"] ?? r["name"] ?? "").trim();
        if (!name) return null;
        return {
          name,
          quantity: Number(r["Miktar"] ?? r["Adet"] ?? r["quantity"] ?? 1) || 1,
          unit: (String(r["Birim"] ?? r["unit"] ?? "ADET").trim().toUpperCase()) || "ADET",
          description: String(r["Açıklama"] ?? r["Aciklama"] ?? r["description"] ?? "").trim(),
        };
      }).filter(Boolean);
      if (!items.length) return toast.error("Excelde geçerli satır yok ('Ürün Adı' sütunu gerekli)");
      setNf((f) => ({ ...f, items: [...f.items, ...items] }));
      toast.success(`${items.length} satır Excel'den eklendi`);
    } catch { toast.error("Excel okunamadı"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const toggleSel = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSel(new Set());
  const bulkDelete = async () => {
    if (!sel.size || !await confirm(`${sel.size} liste silinsin mi?`)) return;
    try { await Promise.all([...sel].map((id) => api.delete(`/prep-lists/${id}`))); toast.success(`${sel.size} liste silindi`); clearSel(); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const bulkReactivate = async () => {
    if (!sel.size) return;
    try { await Promise.all([...sel].map((id) => api.put(`/prep-lists/${id}`, { status: "hazirlaniyor" }))); toast.success(`${sel.size} liste tekrar aktif edildi`); clearSel(); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const setItems = (list, items) => setLists((prev) => prev.map((l) => (l.id === list.id ? { ...l, items } : l)));

  const toggleItem = async (list, idx) => {
    const items = list.items.map((it, i) => {
      if (i !== idx) return it;
      const reqd = Number(it.quantity) || 0;
      const np = !it.prepared;
      return { ...it, prepared: np, delivered: np ? reqd : 0 };
    });
    setItems(list, items);
    try { await api.put(`/prep-lists/${list.id}`, { items }); } catch (e) { toast.error(apiError(e)); load(); }
  };
  // Verilen miktar — yazarken yerel güncelle, blur'da kaydet (metraj ile aynı desen). verilen>=istenen → otomatik hazır.
  const setDeliveredLocal = (list, idx, raw) => {
    const items = list.items.map((it, i) => {
      if (i !== idx) return it;
      const reqd = Number(it.quantity) || 0;
      const d = raw === "" ? 0 : Math.max(0, Number(raw) || 0);
      return { ...it, delivered: raw === "" ? 0 : d, prepared: reqd > 0 && d >= reqd };
    });
    setItems(list, items);
  };
  const setDeliveredFull = async (list, idx) => {
    const items = list.items.map((it, i) => {
      if (i !== idx) return it;
      const reqd = Number(it.quantity) || 0;
      return { ...it, delivered: reqd, prepared: reqd > 0 };
    });
    setItems(list, items);
    try { await api.put(`/prep-lists/${list.id}`, { items }); } catch (e) { toast.error(apiError(e)); load(); }
  };
  const patchItem = (list, idx, patch) => {
    const items = list.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    setItems(list, items);
  };
  const persist = (list) => api.put(`/prep-lists/${list.id}`, { items: list.items }).catch((e) => { toast.error(apiError(e)); load(); });
  const markAll = async (list, val) => {
    const items = list.items.map((it) => { const reqd = Number(it.quantity) || 0; return { ...it, prepared: val, delivered: val ? reqd : 0 }; });
    setItems(list, items);
    try { await api.put(`/prep-lists/${list.id}`, { items }); } catch (e) { toast.error(apiError(e)); load(); }
  };
  const complete = (list) => setCompleteTarget(list);
  const confirmComplete = async (deduct) => {
    const list = completeTarget;
    if (!list) return;
    const saleLinked = !!(list.cart_id || list.quick_sale_id);
    try {
      const { data } = await api.put(`/prep-lists/${list.id}`, { status: "tamamlandi", deduct_stock: saleLinked ? false : deduct });
      toast.success(!saleLinked && deduct ? `Tamamlandı — ${data?.deducted ?? 0} kalem stoktan düşüldü` : "Hazırlık tamamlandı");
      setCompleteTarget(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { if (await confirm("Listeyi sil?")) { await api.delete(`/prep-lists/${id}`); load(); } };

  const markOOS = async (list, idx, val) => {
    try {
      const { data } = await api.post(`/prep-lists/${list.id}/item-oos`, { index: idx, out_of_stock: val });
      if (data) setLists((prev) => prev.map((l) => (l.id === list.id ? data : l)));
      toast.success(val ? "Ürün stokta yok — sipariş talebine eklendi" : "Stokta yok işareti kaldırıldı");
    } catch (e) { toast.error(apiError(e)); }
  };
  const openBulkOOS = (list) => { setOosSel(new Set()); setOosDlg(list); };
  const toggleOosSel = (idx) => setOosSel((p) => { const n = new Set(p); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });
  const confirmBulkOOS = async () => {
    if (!oosDlg || !oosSel.size) return toast.error("En az bir ürün seçin");
    try {
      const { data } = await api.post(`/prep-lists/${oosDlg.id}/items-oos`, { indexes: [...oosSel] });
      if (data) setLists((prev) => prev.map((l) => (l.id === oosDlg.id ? data : l)));
      toast.success(`${data?.oos_created ?? oosSel.size} ürün stokta yok — sipariş talepleri girildi`);
      setOosDlg(null);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openEdit = (list) => {
    setEf({
      id: list.id,
      title: list.customer_title || "",
      assigned: list.assigned_to || "all",
      note: list.note || "",
      items: (list.items || []).map((it) => ({ ...it, quantity: it.quantity ?? 1, unit: it.unit || "ADET" })),
    });
    setEditDlg(true);
  };
  const setEfItem = (i, k, v) => setEf((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));
  const rmEfItem = (i) => setEf((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const addEfManual = () => setEf((f) => ({ ...f, items: [...f.items, { name: "", description: "", quantity: 1, unit: "ADET" }] }));
  const add5Edit = () => setEf((f) => ({ ...f, items: [...f.items, ...Array.from({ length: 5 }, () => ({ name: "", description: "", quantity: 1, unit: "ADET" }))] }));
  const onEditExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
      const items = rows.map((r) => {
        const name = String(r["Ürün Adı"] ?? r["Urun Adi"] ?? r["Ad"] ?? r["name"] ?? "").trim();
        if (!name) return null;
        return {
          name,
          quantity: Number(r["Miktar"] ?? r["Adet"] ?? r["quantity"] ?? 1) || 1,
          unit: (String(r["Birim"] ?? r["unit"] ?? "ADET").trim().toUpperCase()) || "ADET",
          description: String(r["Açıklama"] ?? r["Aciklama"] ?? r["description"] ?? "").trim(),
        };
      }).filter(Boolean);
      if (!items.length) return toast.error("Excelde geçerli satır yok ('Ürün Adı' sütunu gerekli)");
      setEf((f) => ({ ...f, items: [...f.items, ...items] }));
      toast.success(`${items.length} satır Excel'den eklendi`);
    } catch { toast.error("Excel okunamadı"); }
    finally { if (editFileRef.current) editFileRef.current.value = ""; }
  };
  const saveEdit = async () => {
    const items = ef.items.filter((it) => (it.name || "").trim()).map((it) => ({ ...it, quantity: Number(it.quantity) || 0 }));
    if (!items.length) return toast.error("En az bir kalem olmalı");
    try {
      await api.put(`/prep-lists/${ef.id}`, {
        customer_title: ef.title.trim() || "Hazırlık Listesi",
        assigned_to: ef.assigned === "all" ? "" : ef.assigned,
        note: ef.note,
        items,
      });
      toast.success("Liste güncellendi");
      setEditDlg(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const q = search.trim().toLocaleLowerCase("tr");
  const match = (l) => !q || [l.quote_no, l.customer_title, l.assigned_name].some((v) => (v || "").toLocaleLowerCase("tr").includes(q));
  const active = lists.filter((l) => l.status !== "tamamlandi" && match(l));
  const done = lists.filter((l) => l.status === "tamamlandi" && match(l));
  const totalItems = lists.reduce((s, l) => s + l.items.filter((i) => !i.removed).length, 0);
  const preparedItems = lists.reduce((s, l) => s + l.items.filter((i) => i.prepared && !i.removed).length, 0);
  const donePct = totalItems ? Math.round((preparedItems / totalItems) * 100) : 0;
  const activeCount = lists.filter((l) => l.status !== "tamamlandi").length;
  const doneCount = lists.filter((l) => l.status === "tamamlandi").length;

  const cardProps = { isAdmin, onToggleSel: toggleSel, onToggleItem: toggleItem, onPatchItem: patchItem, onPersist: persist, onComplete: complete, onDelete: del, onMarkAll: markAll, onEdit: openEdit, onMarkOOS: markOOS, onBulkOOS: openBulkOOS, onSetDelivered: setDeliveredLocal, onSetDeliveredFull: setDeliveredFull, onChat: openChat, onAck: ack };
  const oosTotal = lists.filter((l) => l.status !== "tamamlandi").reduce((s, l) => s + l.items.filter((i) => i.out_of_stock).length, 0);
  const showActive = view !== "done", showDone = view !== "active";

  return (
    <div className="space-y-6 stagger-in" data-testid="prep-page">
      <PageHeader title="Hazırlanacak Listeler" badge="Depo Hazırlık" subtitle={isAdmin ? "Teklif, cari veya stoktan/elle oluşturduğunuz fiyatsız hazırlık listeleri · kalem kalem işaretle, metraj gir, stokta yoksa tek tıkla talep aç" : "Yöneticinin hazırlamanız için ilettiği malzeme listeleri · hazırladıkça işaretleyin"}>
        {<Button onClick={openNew} data-testid="new-prep-btn"><Plus className="h-4 w-4 mr-2" />Yeni Liste</Button>}
        <Button variant="outline" onClick={() => setCalcOpen(true)} data-testid="open-calculator"><Calculator className="h-4 w-4 mr-2" />Hesap Makinesi</Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="prep-kpis">
        <StatCard label="Aktif Liste" value={activeCount} icon={ClipboardCheck} tone="primary" sub={oosTotal ? `${oosTotal} kalem stokta yok` : "Tüm kalemler stokta"} onClick={() => setView("active")} active={view === "active"} testid="prep-kpi-active" />
        <StatCard label="Tamamlanan" value={doneCount} icon={CheckCircle2} tone="success" sub="Teslim edilen listeler" onClick={() => setView("done")} active={view === "done"} testid="prep-kpi-done" />
        <StatCard label="Toplam Kalem" value={NUM(totalItems)} icon={Package} sub={`${NUM(preparedItems)} hazırlandı`} onClick={() => setView("all")} active={view === "all"} testid="prep-kpi-items" />
        <StatCard label="Hazırlanma Oranı" value={`%${donePct}`} icon={Percent} tone="blue" sub="Tüm listeler" testid="prep-kpi-rate" />
      </div>

      {lists.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative w-full sm:max-w-md" data-testid="prep-search-wrap">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Teklif no, cari veya personel ara..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" data-testid="prep-search" />
          </div>
          <div className="sm:ml-auto"><Segmented value={view} onChange={setView} options={[["active", "Aktif", activeCount], ["done", "Tamamlanan", doneCount], ["all", "Tümü", lists.length]]} testid="prep-view" /></div>
        </div>
      )}

      {isAdmin && sel.size > 0 && (
        <div className="flex items-center gap-3 bg-primary text-white rounded-lg px-4 py-3" data-testid="prep-bulk-bar">
          <span className="font-semibold text-sm">{sel.size} liste seçildi</span>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="secondary" className="bg-white text-primary hover:bg-white/90" onClick={bulkReactivate} data-testid="prep-bulk-reactivate"><RefreshCw className="h-4 w-4 mr-1" />Tekrar Aktif Et</Button>
            <Button size="sm" variant="outline" className="border-white/40 text-white hover:bg-white/10" onClick={bulkDelete} data-testid="prep-bulk-delete"><Trash2 className="h-4 w-4 mr-1" />Sil</Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={clearSel}>Temizle</Button>
          </div>
        </div>
      )}

      {lists.length === 0 ? (
        <Empty title="Liste yok" desc={isAdmin ? "Teklif, cari veya 'Yeni Liste' ile stoktan/elle liste oluşturun." : "Depo için 'Yeni Liste' ile elle veya barkodla hazırlık listesi oluşturabilirsiniz."} action={<Button onClick={openNew} data-testid="prep-empty-new"><Plus className="h-4 w-4 mr-2" />Yeni Liste</Button>} />
      ) : (
        <>
          {((showActive ? active.length : 0) + (showDone ? done.length : 0)) === 0 && (
            <p className="text-sm text-muted-foreground border border-dashed rounded-lg p-6 text-center" data-testid="prep-no-match">{q ? "Aramanızla eşleşen liste bulunamadı." : view === "done" ? "Henüz tamamlanan liste yok." : "Aktif liste yok — tüm listeler tamamlandı."}</p>
          )}
          {showActive && active.length > 0 && (
            <div>
              <h3 className="font-head text-sm font-bold uppercase tracking-wider text-slate-600 mb-3 flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-primary" />Aktif <span className="tabular-nums rounded-full bg-primary/10 text-primary px-1.5 py-px text-[10px]">{active.length}</span></h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{active.map((l) => <PrepCard key={l.id} list={l} selected={sel.has(l.id)} {...cardProps} />)}</div>
            </div>
          )}
          {showDone && done.length > 0 && (
            <div>
              <h3 className="font-head text-sm font-bold uppercase tracking-wider text-slate-600 mb-3 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#16A34A]" />Tamamlanan <span className="tabular-nums rounded-full bg-emerald-50 text-emerald-700 px-1.5 py-px text-[10px]">{done.length}</span></h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{done.map((l) => <PrepCard key={l.id} list={l} selected={sel.has(l.id)} {...cardProps} />)}</div>
            </div>
          )}
        </>
      )}

      <Dialog open={!!completeTarget} onOpenChange={(o) => !o && setCompleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="prep-complete-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl flex items-center gap-2"><CheckCircle2 className="h-6 w-6 text-[#16A34A]" />Hazırlığı Tamamla</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {completeTarget && (completeTarget.cart_id || completeTarget.quick_sale_id)
              ? <><b>{completeTarget?.customer_title}</b> listesi Hızlı Satış'tan geldi — stok satışta düşülür, <b>tekrar düşülmez</b>. Fişte "Hazırlandı ✓" görünür.</>
              : <><b>{completeTarget?.customer_title}</b> listesi tamamlanıyor. Bu listedeki ürünler <b>stoktan düşülsün mü?</b> (Ürün adı stoktaki bir ürünle eşleşenler düşülür.)</>}
          </p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            {completeTarget && (completeTarget.cart_id || completeTarget.quick_sale_id) ? (
              <Button className="bg-[#16A34A] hover:bg-[#15803d]" onClick={() => confirmComplete(false)} data-testid="prep-complete-sale"><CheckCircle2 className="h-4 w-4 mr-2" />Hazırlığı Tamamla</Button>
            ) : (<>
              <Button variant="outline" onClick={() => confirmComplete(false)} data-testid="prep-complete-nostock">Hayır, Sadece Tamamla</Button>
              <Button className="bg-[#16A34A] hover:bg-[#15803d]" onClick={() => confirmComplete(true)} data-testid="prep-complete-deduct"><Package className="h-4 w-4 mr-2" />Evet, Stoktan Düş</Button>
            </>)}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={newDlg} onOpenChange={setNewDlg}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="new-prep-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl flex items-center gap-2"><ClipboardCheck className="h-6 w-6 text-primary" />Yeni Hazırlık Listesi</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Başlık / Cari (opsiyonel)</Label>
                <Input value={nf.title} onChange={(e) => setNf({ ...nf, title: e.target.value })} placeholder="Örn. Ahmet Bey Şantiye" data-testid="new-prep-title" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Kime gönderilsin?</Label>
                <Select value={nf.assigned} onValueChange={(v) => setNf({ ...nf, assigned: v })}>
                  <SelectTrigger data-testid="new-prep-worker"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Depo Yöneticileri</SelectItem>
                    {workers.map((w) => <SelectItem key={w.id} value={w.id}><span className="flex items-center gap-1"><UserRound className="h-3 w-3" />{w.name}</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><StickyNote className="h-3.5 w-3.5 text-amber-500" />Liste Notu (genel — tüm listeye)</Label>
              <textarea value={nf.note} onChange={(e) => setNf({ ...nf, note: e.target.value })} rows={2} placeholder="Tüm liste için not — ör. Bugün öğleden sonra teslim, müşteri gelip alacak" className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm resize-y" data-testid="new-prep-note" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Stoktan Ürün Çağır</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input className="pl-9" placeholder="Ürün adı, kod veya marka ara..." value={stockQ} onChange={(e) => setStockQ(e.target.value)} data-testid="new-prep-stock-search" />
              </div>
              {stockRes.length > 0 && (
                <div className="border rounded-lg overflow-y-auto overscroll-contain max-h-60" data-testid="new-prep-stock-results">
                  {stockRes.map((p) => (
                    <button key={p.id} onClick={() => addStock(p)} className="w-full text-left px-3 py-2 hover:bg-muted flex items-center justify-between border-b last:border-0" data-testid={`new-prep-stock-item-${p.id}`}>
                      <span className="min-w-0"><span className="text-sm font-semibold block leading-snug break-words" title={p.name}>{p.name}</span><span className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">{[p.brand, p.code].filter(Boolean).join(" · ") || "—"}<StockSplit p={p} total testid={`new-prep-stock-split-${p.id}`} /></span></span>
                      <PackagePlus className="h-4 w-4 text-primary shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><ScanLine className="h-3.5 w-3.5" />Barkod ile Ekle</Label>
              <div className="flex gap-2 items-start">
                <div className="flex-1 min-w-0"><BarcodeInput onResolve={async (code) => { const n = await addCodes([code]); return { ok: n > 0, label: n > 0 ? "Eklendi" : `Yok: ${code}` }; }} placeholder="Barkod okut + Enter (anında eklenir)" testid="new-prep-barcode-input" className="w-full" /></div>
                <Button type="button" variant="outline" onClick={() => setBcCam(true)} data-testid="new-prep-barcode-camera"><ScanLine className="h-4 w-4 mr-1" />Kamera</Button>
              </div>
              <textarea value={bcText} onChange={(e) => setBcText(e.target.value)} rows={2} placeholder="Toplu: her satıra bir barkod yapıştırın, sonra Barkodları Ekle" className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm font-mono resize-y" data-testid="new-prep-barcode-text" />
              <Button size="sm" variant="outline" onClick={addByBarcodes} disabled={!bcText.trim()} data-testid="new-prep-barcode-add"><ScanLine className="h-4 w-4 mr-1" />Barkodları Ekle</Button>
              <BarcodeScanner open={bcCam} continuous onClose={() => setBcCam(false)} onDetected={(c) => addCodes([String(c).trim()])} />
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Kalemler ({nf.items.length})</Label>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="new-prep-template"><FileSpreadsheet className="h-4 w-4 mr-1" />Şablon Oluştur</Button>
                <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="new-prep-upload"><Upload className="h-4 w-4 mr-1" />Excel Yükle</Button>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onExcel} data-testid="new-prep-file" />
                <Button size="sm" variant="outline" onClick={addManual} data-testid="new-prep-add-manual"><Plus className="h-4 w-4 mr-1" />Satır</Button>
                <Button size="sm" onClick={add5Manual} data-testid="new-prep-add-5"><Plus className="h-4 w-4 mr-1" />+5 Satır</Button>
              </div>
            </div>
            {nf.items.length === 0 ? (
              <p className="text-sm text-muted-foreground border border-dashed rounded-lg p-4 text-center">Stoktan ürün çağırın veya elle satır ekleyin.</p>
            ) : (
              <div className="space-y-2">
                {nf.items.map((it, i) => (
                  <div key={i} className="rounded-lg border p-2 space-y-1.5" data-testid={`new-prep-item-${i}`}>
                    <div className="flex items-center gap-2">
                      <Input className="flex-1" placeholder="Malzeme adı" value={it.name} onChange={(e) => setNfItem(i, "name", e.target.value)} data-testid={`new-prep-item-name-${i}`} />
                      <Input type="number" className="w-20 tabular-nums" value={it.quantity} onChange={(e) => setNfItem(i, "quantity", e.target.value)} data-testid={`new-prep-item-qty-${i}`} />
                      <Select value={it.unit} onValueChange={(v) => setNfItem(i, "unit", v)}>
                        <SelectTrigger className="w-28" data-testid={`new-prep-item-unit-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{unitOpts(it.unit).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button size="icon" variant="ghost" onClick={() => rmNfItem(i)} className="h-9 w-9 shrink-0"><X className="h-4 w-4 text-destructive" /></Button>
                    </div>
                    <div className="relative">
                      <StickyNote className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-amber-500" />
                      <Input className="h-8 text-xs pl-8" placeholder="Açıklama (opsiyonel) — ör. sarı-yeşil, 2. kat, acele" value={it.description || ""} onChange={(e) => setNfItem(i, "description", e.target.value)} data-testid={`new-prep-item-desc-${i}`} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDlg(false)}>İptal</Button>
            <Button onClick={createList} data-testid="new-prep-save"><Truck className="h-4 w-4 mr-2" />Depoya Gönder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isAdmin && (
      <Dialog open={editDlg} onOpenChange={setEditDlg}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="edit-prep-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl flex items-center gap-2"><Pencil className="h-6 w-6 text-primary" />Hazırlık Listesini Düzenle</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Başlık / Cari</Label>
                <Input value={ef.title} onChange={(e) => setEf({ ...ef, title: e.target.value })} placeholder="Örn. Ahmet Bey Şantiye" data-testid="edit-prep-title" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Personel</Label>
                <Select value={ef.assigned} onValueChange={(v) => setEf({ ...ef, assigned: v })}>
                  <SelectTrigger data-testid="edit-prep-worker"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Depo Yöneticileri</SelectItem>
                    {workers.map((w) => <SelectItem key={w.id} value={w.id}><span className="flex items-center gap-1"><UserRound className="h-3 w-3" />{w.name}</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><StickyNote className="h-3.5 w-3.5 text-amber-500" />Liste Notu (genel — tüm listeye)</Label>
              <textarea value={ef.note} onChange={(e) => setEf({ ...ef, note: e.target.value })} rows={2} placeholder="Tüm liste için not — ör. Bugün öğleden sonra teslim, müşteri gelip alacak" className="w-full rounded-md border border-input bg-card px-3 py-2 text-sm resize-y" data-testid="edit-prep-note" />
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Kalemler ({ef.items.length})</Label>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => editFileRef.current?.click()} data-testid="edit-prep-upload"><Upload className="h-4 w-4 mr-1" />Excel Yükle</Button>
                <input ref={editFileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onEditExcel} data-testid="edit-prep-file" />
                <Button size="sm" variant="outline" onClick={addEfManual} data-testid="edit-prep-add-manual"><Plus className="h-4 w-4 mr-1" />Satır</Button>
                <Button size="sm" onClick={add5Edit} data-testid="edit-prep-add-5"><Plus className="h-4 w-4 mr-1" />+5 Satır</Button>
              </div>
            </div>
            {ef.items.length === 0 ? (
              <p className="text-sm text-muted-foreground border border-dashed rounded-lg p-4 text-center">Kalem yok — satır ekleyin.</p>
            ) : (
              <div className="space-y-2">
                {ef.items.map((it, i) => (
                  <div key={i} className="rounded-lg border p-2 space-y-1.5" data-testid={`edit-prep-item-${i}`}>
                    <div className="flex items-center gap-2">
                      <Input className="flex-1" placeholder="Malzeme adı" value={it.name} onChange={(e) => setEfItem(i, "name", e.target.value)} data-testid={`edit-prep-item-name-${i}`} />
                      <Input type="number" className="w-20 tabular-nums" value={it.quantity} onChange={(e) => setEfItem(i, "quantity", e.target.value)} data-testid={`edit-prep-item-qty-${i}`} />
                      <Select value={it.unit} onValueChange={(v) => setEfItem(i, "unit", v)}>
                        <SelectTrigger className="w-28" data-testid={`edit-prep-item-unit-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{unitOpts(it.unit).map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button size="icon" variant="ghost" onClick={() => rmEfItem(i)} className="h-9 w-9 shrink-0"><X className="h-4 w-4 text-destructive" /></Button>
                    </div>
                    <div className="relative">
                      <StickyNote className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-amber-500" />
                      <Input className="h-8 text-xs pl-8" placeholder="Açıklama (opsiyonel) — ör. sarı-yeşil, 2. kat, acele" value={it.description || ""} onChange={(e) => setEfItem(i, "description", e.target.value)} data-testid={`edit-prep-item-desc-${i}`} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDlg(false)}>İptal</Button>
            <Button onClick={saveEdit} data-testid="edit-prep-save"><CheckCircle2 className="h-4 w-4 mr-2" />Değişiklikleri Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      <Dialog open={!!oosDlg} onOpenChange={(o) => !o && setOosDlg(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="prep-bulk-oos-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl flex items-center gap-2"><PackageX className="h-6 w-6 text-amber-600" />Toplu "Stokta Yok"</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">Stokta olmayan ürünleri seçin. Seçilenler için tek seferde sipariş talepleri oluşturulur.</p>
          <div className="space-y-1.5">
            {(oosDlg?.items || []).map((it, idx) => it.out_of_stock || it.removed ? null : (
              <label key={idx} className="flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer hover:bg-muted/50" data-testid={`prep-oos-pick-${idx}`}>
                <Checkbox checked={oosSel.has(idx)} onCheckedChange={() => toggleOosSel(idx)} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{it.name}</div>
                  {it.description && <div className="text-xs text-muted-foreground truncate">{it.description}</div>}
                </div>
                <span className="text-sm font-bold tabular-nums whitespace-nowrap">{NUM(it.quantity)} {it.unit}</span>
              </label>
            ))}
            {(oosDlg?.items || []).every((it) => it.out_of_stock) && <p className="text-sm text-muted-foreground text-center py-4">Tüm ürünler zaten stokta yok işaretli.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOosDlg(null)}>İptal</Button>
            <Button className="bg-amber-500 hover:bg-amber-600" onClick={confirmBulkOOS} disabled={!oosSel.size} data-testid="prep-bulk-oos-confirm"><PackageX className="h-4 w-4 mr-2" />{oosSel.size} Ürünü İşaretle</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LiveChangeAlert items={changeAlert} onShow={showList} onDismiss={() => setChangeAlert(null)} />
      <Dialog open={calcOpen} onOpenChange={setCalcOpen}>
        <DialogContent className="max-w-xs" data-testid="calculator-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">Hesap Makinesi</DialogTitle></DialogHeader>
          <CalcComponent />
        </DialogContent>
      </Dialog>
      <CartChatSheet open={!!chatList} onOpenChange={(o) => { if (!o) { setChatList(null); load(); } }} side="prep" cl={chatCl} userId={user?.id}
        title={chatList?.customer_title || ""} subtitle={chatList ? `${chatList.cart_owner_name || ""} sepeti · ${chatList.quote_no}` : ""}
        products={(chatList?.items || []).filter((i) => !i.removed).map((i) => ({ key: i.key || i.product_id || i.name, name: i.name }))} />
    </div>
  );
}
